import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { DialogContent, DialogTitle } from '@/components/ui/dialog';
import { ModalLayout, ModalTitle } from '@/components/ui/modal-layout';
import { Button } from '@/components/ui/button';
import { Form } from '../../Form';
import { useFormContext } from '../../Form/FormProvider';
import { useTranslation } from '@hooks/useTranslation';
import {
  createOrderSourceFormSchema,
  type CreateOrderSourceFormData,
} from '../../../schemas/orderSource.schema';
import { OrderSourceFormFields } from './OrderSourceFormFields';

interface CreateOrderSourceFormProps {
  isPending: boolean;
  onSubmit: (values: CreateOrderSourceFormData) => Promise<void>;
  onClose: () => void;
}

const EMPTY_FORM_VALUES = {
  name: '',
  description: null,
  fee: null as unknown as number,
  fee_type: null as unknown as CreateOrderSourceFormData['fee_type'],
} satisfies Partial<CreateOrderSourceFormData>;

function FormFooter({ isPending }: { isPending: boolean }) {
  const { form } = useFormContext();
  const { t } = useTranslation();

  return (
    <form.Subscribe
      selector={(state) => ({
        canSubmit: state.canSubmit,
        isSubmitting: state.isSubmitting,
      })}
    >
      {({ canSubmit, isSubmitting }) => {
        const isDisabled = !canSubmit || isSubmitting || isPending;

        return (
          <div className="flex w-full items-center justify-end">
            <Button
              type="submit"
              disabled={isDisabled}
            >
              {t('create_modal.create_form.create')}
            </Button>
          </div>
        );
      }}
    </form.Subscribe>
  );
}

export function CreateOrderSourceForm({
  isPending,
  onSubmit,
  onClose,
}: CreateOrderSourceFormProps) {
  const { t } = useTranslation();
  const title = t('create_modal.create_form.title', 'Create new source');

  return (
    <DialogContent
      aria-describedby={undefined}
      onEscapeKeyDown={(e) => {
        if (isPending) {
          e.preventDefault();
          return;
        }
        onClose();
      }}
      onInteractOutside={(e) => {
        const target = e.target as HTMLElement;
        if (target.closest('[data-radix-popper-content-wrapper]')) {
          e.preventDefault();
          return;
        }
        // Block backdrop-close while a submit is in flight; the request
        // would still resolve server-side after the dialog unmounted.
        if (isPending) {
          e.preventDefault();
        }
      }}
      className="max-w-dialog max-h-[90vh] rounded-input border-dinnerplate-white bg-body p-0 outline-none shadow-none [&>button:last-child]:hidden"
    >
      <VisuallyHidden.Root>
        <DialogTitle>{title}</DialogTitle>
      </VisuallyHidden.Root>
      <Form
        schema={createOrderSourceFormSchema}
        defaultValues={EMPTY_FORM_VALUES}
        onSubmit={onSubmit}
        isPending={isPending}
        className="flex flex-1 flex-col min-h-0"
      >
        <ModalLayout
          as="div"
          title={<ModalTitle>{title}</ModalTitle>}
          onClose={onClose}
          footer={<FormFooter isPending={isPending} />}
        >
          <OrderSourceFormFields />
        </ModalLayout>
      </Form>
    </DialogContent>
  );
}
